/**
 * Lightweight crash/error reporting for the AR launch + scene.
 *
 * Always logs to the console (so it's visible over chrome://inspect on a real
 * device) and, on non-dev builds where a Sentry DSN is configured, forwards to
 * Sentry. Sentry itself is initialised in main.tsx; calling `captureException`
 * here reuses that already-initialised client. Safe to call anywhere — these
 * helpers never throw.
 *
 * NOTE: report genuine, unexpected failures here (content-load throws, lost
 * WebGL context). Do NOT route expected user-facing conditions — a denied AR
 * prompt, an unsupported device — through this; those surface in the UI via the
 * xr-error events and would just be Sentry noise.
 */

const sentryEnabled = !import.meta.env.DEV && !!import.meta.env.VITE_SENTRY_DSN;

/**
 * Resolve the Sentry module once its client is initialised. main.tsx inits
 * Sentry from an UN-awaited dynamic import, so an early caller (e.g. the
 * stale-launch report fired on app mount) can run before init lands — and
 * captureException on a client-less SDK silently drops the event. Poll briefly
 * (bounded) for the client so those early, high-value events aren't lost.
 */
async function getReadySentry(): Promise<typeof import("@sentry/browser")> {
  const Sentry = await import("@sentry/browser");
  for (let i = 0; i < 30 && !Sentry.getClient(); i++) {
    await new Promise((r) => setTimeout(r, 100));
  }
  return Sentry;
}

export function reportError(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(typeof error === "string" ? error : String(error));
  // eslint-disable-next-line no-console
  console.error("[ar-report]", context?.where ?? "", err, context ?? "");
  if (!sentryEnabled) return;
  void getReadySentry()
    .then((Sentry) => {
      Sentry.captureException(err, { tags: { area: "ar" }, extra: context });
    })
    .catch(() => {
      /* reporting must never throw */
    });
}

/**
 * Record a non-error milestone. Attached as a breadcrumb to whatever Sentry
 * event fires next, so a later crash report shows how far the launch got.
 */
export function reportBreadcrumb(message: string, data?: Record<string, unknown>): void {
  if (!sentryEnabled) return;
  void import("@sentry/browser")
    .then((Sentry) => {
      Sentry.addBreadcrumb({ category: "ar", level: "info", message, data });
    })
    .catch(() => {
      /* never throw */
    });
}
